import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { prop, uniqBy } from 'ramda'
import { SyntheticEvent } from 'react'
import { Case, Switch, When } from 'react-if'
import { useParams } from 'react-router-dom'

import { ButtonLink, Icon, SearchToolbar, useConfirm } from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useAppsyncRefetch, useUrlParams } from '@/hooks'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { logInfo, safeCypressID, titleCase } from '@/utils'
import { ProcessingStatus } from '@/utils/api'
import splitAndTrim from '@/utils/splitAndTrim'

import {
  DownloadQuestions,
  DownloadSampleCSV,
  NewSemanticQuestion,
  SemanticSearchSettings,
  SemanticSearchUploader,
} from '../components'

function useSemanticQuestions() {
  const [{ questionSelected }, setUrlParams] = useUrlParams()
  const { componentID } = useComponentProvider()
  const confirm = useConfirm()
  const semanticSearch =
    useGetCustomComponent<SemanticSearchSettings>(componentID)
  const updateSemanticSearch =
    useUpdateCustomComponent<SemanticSearchSettings>(componentID)

  const selectQuestion =
    (question: { key: String }) => (_: SyntheticEvent, isExpanded: boolean) => {
      setUrlParams({
        questionSelected: isExpanded ? question?.key ?? null : null,
      })
    }

  const deleteQuestion = async () => {
    if (!questionSelected) return

    try {
      await confirm({
        cancellationButtonProps: {
          color: 'warning',
          variant: 'contained',
        },
        description: 'This will delete the question.',
        title: 'Are you sure?',
      })
      if (componentID) {
        const newQuestions =
          semanticSearch.data?.settings?.questions?.filter(
            (question) => question?.key !== questionSelected
          ) ?? []
        await updateSemanticSearch.mutateAsync({
          id: componentID,
          settings: {
            ...semanticSearch.data?.settings,
            questions: newQuestions,
          },
        })
      }
    } catch (e) {
      logInfo(e)
    }
  }

  return {
    deleteQuestion,
    questionSelected,
    questions: semanticSearch.data?.settings?.questions ?? [],
    selectQuestion,
    semanticSearch,
  }
}

export function Questions() {
  const { componentID } = useComponentProvider()
  const {
    questionSelected,
    questions,
    selectQuestion,
    deleteQuestion,
    semanticSearch,
  } = useSemanticQuestions()
  const [, setUrlParams] = useUrlParams()

  const splitComma = splitAndTrim(',')
  const refetching = useAppsyncRefetch((state) => state.refetching)

  const missingAnswers = questions
    .map((q) => {
      if (!q.answerText) return prop('key', q)
      return false
    })
    .filter(Boolean) as string[]

  const { orgID } = useParams()

  return (
    <Stack pt={5}>
      <SearchToolbar searchTitle="Search Questions" />
      <DownloadSampleCSV />
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ mb: 2 }} variant="h6">
          Questions
        </Typography>
        <When
          condition={semanticSearch.data?.status === ProcessingStatus.error}
        >
          <Alert severity="error">{semanticSearch.data?.displayStatus}</Alert>
        </When>
      </Stack>
      {questions?.map((question) => {
        if (!question) return null

        const sources = splitComma(question.source).filter(Boolean)
        const isGeneratingAnswers =
          !!refetching?.[`${componentID}_${question.key}`]
        return (
          <Accordion
            key={question.key}
            disabled={isGeneratingAnswers}
            expanded={questionSelected === question.key}
            id={safeCypressID(`accordion-${question.question}`)}
            onChange={selectQuestion(question)}
          >
            <AccordionSummary>
              <Box sx={{ alignItems: 'center', display: 'flex' }}>
                <Typography
                  component="span"
                  id={safeCypressID(`accordion title ${question.question}`)}
                >
                  {question.key} {question.question}{' '}
                </Typography>

                {isGeneratingAnswers ? (
                  <Chip
                    icon={<Icon name="loading" />}
                    label="Generating"
                    sx={{ ml: 2 }}
                  />
                ) : null}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <When condition={questionSelected === question.key}>
                <Box display="grid" gap={2}>
                  <TextField
                    disabled
                    fullWidth
                    multiline
                    label="Question"
                    maxRows={3}
                    minRows={1}
                    value={question.question}
                  />
                  <Box id={safeCypressID(`${question.question} answer`)}>
                    <Typography component="span" fontWeight="bold">
                      Answer:
                    </Typography>
                    {question.answerText?.split('\n').map((text) => (
                      <Typography
                        key={text}
                        id={safeCypressID(`answer ${text}`)}
                      >
                        {' '}
                        {text}
                      </Typography>
                    ))}
                  </Box>
                  <Box id={safeCypressID(`${question.question} relatedEgs`)}>
                    <Typography component="p" fontWeight="bold">
                      Related Engagement Goals:
                    </Typography>
                    {Array.isArray(question?.relatedEgs) &&
                      uniqBy(prop('egName'), question.relatedEgs).map(
                        (eg) =>
                          !!eg.egID && (
                            <ButtonLink
                              key={eg.egName}
                              sx={{ textTransform: 'unset' }}
                              to={`/${orgID}/engagement-goals/${eg.egID}`}
                              variant="text"
                            >
                              {titleCase(eg.egName)}
                            </ButtonLink>
                          )
                      )}
                  </Box>

                  <Box id={safeCypressID(`${question.question} related`)}>
                    <Typography component="p" fontWeight="bold">
                      Related Questions:
                    </Typography>

                    {Array.isArray(question?.suggestedQuestions)
                      ? question.suggestedQuestions.map((q) => {
                          const qFound = questions.find(({ key }) => key === q)
                          if (!qFound) return null
                          const text = `${qFound.key} - ${qFound.question}`
                          return (
                            <Button
                              key={text}
                              sx={{ textTransform: 'unset' }}
                              variant="text"
                              onClick={() =>
                                setUrlParams({ questionSelected: qFound.key })
                              }
                            >
                              {text}
                            </Button>
                          )
                        })
                      : null}
                  </Box>
                  <Switch>
                    <Case condition={sources.length === 0}>
                      <Typography
                        id={safeCypressID(`${question.question} source none`)}
                      >
                        <Typography component="span" fontWeight="bold">
                          Source:
                        </Typography>{' '}
                        None
                      </Typography>
                    </Case>
                    <Case condition={sources.length === 1}>
                      <Typography
                        id={safeCypressID(`${question.question} single source`)}
                      >
                        <Typography component="span" fontWeight="bold">
                          Source:
                        </Typography>{' '}
                        {sources}
                      </Typography>
                    </Case>
                    <Case condition={sources.length > 1}>
                      <Typography>
                        <Typography component="span" fontWeight="bold">
                          Source:
                        </Typography>
                        <Stack ml={2}>
                          {sources.map((source) => {
                            const isExternalLink =
                              ['http://', 'https://']
                                .map((method) => source.includes(method))
                                .filter(Boolean).length !== 0
                            return isExternalLink ? (
                              <Link
                                key={source}
                                href={source}
                                id={safeCypressID(
                                  `${question.question} external source ${source}`
                                )}
                                target="_blank"
                              >
                                {source}
                              </Link>
                            ) : (
                              <Typography
                                key={source}
                                id={safeCypressID(
                                  `${question.question} source ${source}`
                                )}
                              >
                                {source}
                              </Typography>
                            )
                          })}
                        </Stack>
                      </Typography>
                    </Case>
                  </Switch>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      color="error"
                      id="delete-question-button"
                      variant="text"
                      onClick={deleteQuestion}
                    >
                      Delete Question
                    </Button>
                    <NewSemanticQuestion
                      buttonText="Edit Question"
                      questionKey={question.key}
                    />
                  </Stack>
                </Box>
              </When>
            </AccordionDetails>
          </Accordion>
        )
      })}
      <Stack direction="row" justifyContent="flex-end" mt={2}>
        <ButtonGroup
          aria-label="outlined primary button group"
          variant="outlined"
        >
          <DownloadQuestions />
          <SemanticSearchUploader />

          <NewSemanticQuestion buttonText="Add Question" />
        </ButtonGroup>
      </Stack>
    </Stack>
  )
}
